import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, hasFxAccess } from '../../components/auth/AuthContext';
import { userService } from '../../services/api/userService';
import type { UserAdminProfile, AdminInviteRequest, AdminUpdateRequest } from '../../services/api/types';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import McpKeyModal from './McpKeyModal';
import './UsersPanel.css';

const ASSIGNABLE_ROLES = [
  'client_admin',
  'client_fo_user',
  'client_bo_user',
  'client_fo_bo_user',
  'client_ro_user',
  'client_acc_user',
] as const;

const UsersPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const digitalSignatureEnabled = user?.profile?.organization?.digitalSignatureEnabled ?? false;

  // Data
  const [users, setUsers] = useState<UserAdminProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState<AdminInviteRequest>({
    email: '', firstName: '', lastName: '', primaryRole: 'client_fo_user', canSignDocuments: false, canSelfApproveModifications: false, jobTitle: '',
  });
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);

  // Column filters
  const [filterName, setFilterName] = useState('');
  const [filterJobTitle, setFilterJobTitle] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterSignatory, setFilterSignatory] = useState('');
  const [filterSelfApprove, setFilterSelfApprove] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMfa, setFilterMfa] = useState('');
  const [filterModule, setFilterModule] = useState('');

  // Show Modules column only when at least one user has moduleRoles (multi-module org)
  const isMultiModule = useMemo(() => users.some(u => u.moduleRoles && Object.keys(u.moduleRoles).length > 0), [users]);

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      if (filterName && !u.fullName.toLowerCase().includes(filterName.toLowerCase())) return false;
      if (filterJobTitle && !(u.jobTitle || '').toLowerCase().includes(filterJobTitle.toLowerCase())) return false;
      if (filterEmail && !u.email.toLowerCase().includes(filterEmail.toLowerCase())) return false;
      if (filterRole && u.primaryRole !== filterRole) return false;
      if (filterSignatory === 'yes' && !u.canSignDocuments) return false;
      if (filterSignatory === 'no' && u.canSignDocuments) return false;
      if (filterSelfApprove === 'yes' && !u.canSelfApproveModifications) return false;
      if (filterSelfApprove === 'no' && u.canSelfApproveModifications) return false;
      if (filterStatus && u.status !== filterStatus) return false;
      if (filterMfa === 'required' && !u.mfaRequired) return false;
      if (filterMfa === 'off' && u.mfaRequired) return false;
      if (filterModule) {
        if (filterModule === '_fx_only') {
          // FX only = has FX access but no additional modules
          if (!hasFxAccess(u)) return false;
          const extraModules = u.moduleRoles ? Object.keys(u.moduleRoles).filter(m => m !== 'FX') : [];
          if (extraModules.length > 0) return false;
        } else if (filterModule === 'FX') {
          if (!hasFxAccess(u)) return false;
        } else {
          // e.g. "Commodities" — check moduleRoles contains it
          const modules = u.moduleRoles ? Object.keys(u.moduleRoles) : [];
          if (!modules.includes(filterModule)) return false;
        }
      }
      return true;
    });
  }, [users, filterName, filterJobTitle, filterEmail, filterRole, filterSignatory, filterSelfApprove, filterStatus, filterMfa, filterModule]);

  const hasActiveFilters = filterName || filterJobTitle || filterEmail || filterRole || filterSignatory || filterSelfApprove || filterStatus || filterMfa || filterModule;

  const clearFilters = () => {
    setFilterName('');
    setFilterJobTitle('');
    setFilterEmail('');
    setFilterRole('');
    setFilterSignatory('');
    setFilterSelfApprove('');
    setFilterStatus('');
    setFilterMfa('');
    setFilterModule('');
  };

  // Inline edit
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<AdminUpdateRequest>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error'; visible: boolean }>({
    message: '', type: 'success', visible: false,
  });

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type, visible: true });
  };

  // MCP Key modal
  const [mcpModalUser, setMcpModalUser] = useState<UserAdminProfile | null>(null);

  // --- Data loading ---

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.adminListUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        setError(response.message || 'Failed to load users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // --- Invite ---

  const openInviteModal = () => {
    setInviteForm({ email: '', firstName: '', lastName: '', primaryRole: 'client_fo_user', canSignDocuments: false, canSelfApproveModifications: false, jobTitle: '', mfaRequired: false });
    setInviteEmailError(null);
    setShowInviteModal(true);
  };

  const handleInvite = async () => {
    setInviteEmailError(null);
    setIsInviting(true);
    try {
      const response = await userService.inviteUser(inviteForm);
      if (response.success) {
        showToast(t('admin.users.inviteSuccess', { email: inviteForm.email }), 'success');
        setShowInviteModal(false);
        loadUsers();
      } else {
        if (response.message?.toLowerCase().includes('already exists')) {
          setInviteEmailError(t('admin.users.emailExists'));
        } else if (response.message?.toLowerCase().includes('does not match')) {
          setInviteEmailError(t('admin.users.domainMismatch'));
        } else {
          showToast(t('admin.users.inviteError'), 'error');
        }
      }
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('already exists')) {
        setInviteEmailError(t('admin.users.emailExists'));
      } else if (msg.toLowerCase().includes('does not match')) {
        setInviteEmailError(t('admin.users.domainMismatch'));
      } else {
        showToast(t('admin.users.inviteError'), 'error');
      }
    } finally {
      setIsInviting(false);
    }
  };

  // --- Inline Edit ---

  const startEditing = (u: UserAdminProfile) => {
    setEditingUid(u.uid);
    // For multi-module orgs: make FX access explicit so checkboxes work cleanly.
    // Legacy users (no moduleRoles) have implicit FX → materialize as moduleRoles.FX.
    let editModuleRoles = u.moduleRoles ? { ...u.moduleRoles } : undefined;
    if (isMultiModule && hasFxAccess(u) && (!editModuleRoles || !editModuleRoles.FX)) {
      editModuleRoles = { FX: u.primaryRole || 'client_ro_user', ...editModuleRoles };
    }
    setEditForm({
      firstName: u.firstName,
      lastName: u.lastName,
      primaryRole: u.primaryRole,
      canSignDocuments: u.canSignDocuments,
      canSelfApproveModifications: u.canSelfApproveModifications,
      jobTitle: u.jobTitle || '',
      moduleRoles: editModuleRoles,
    });
  };

  const cancelEditing = () => {
    setEditingUid(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingUid) return;
    setIsSavingEdit(true);
    try {
      const response = await userService.adminUpdateUser(editingUid, editForm);
      if (response.success) {
        const msg = editForm.canSignDocuments !== undefined
          ? t('admin.users.updateSuccessReloginNote')
          : t('admin.users.updateSuccess');
        showToast(msg, 'success');
        setEditingUid(null);
        setEditForm({});
        loadUsers();
      } else {
        showToast(response.message || 'Failed to update user', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to update user', 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // --- Deactivate / Reactivate ---

  const confirmDeactivate = (u: UserAdminProfile) => {
    setConfirmDialog({
      isOpen: true,
      title: t('admin.users.confirmDeactivate'),
      message: t('admin.users.confirmDeactivateMessage', { name: u.fullName }),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          await userService.deactivateUser(u.uid);
          showToast(t('admin.users.deactivateSuccess', { name: u.fullName }), 'success');
          loadUsers();
        } catch (err: any) {
          showToast(err.message || 'Failed to deactivate user', 'error');
        }
      },
    });
  };

  const confirmReactivate = (u: UserAdminProfile) => {
    setConfirmDialog({
      isOpen: true,
      title: t('admin.users.confirmReactivate'),
      message: t('admin.users.confirmReactivateMessage', { name: u.fullName }),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          await userService.reactivateUser(u.uid);
          showToast(t('admin.users.reactivateSuccess', { name: u.fullName }), 'success');
          loadUsers();
        } catch (err: any) {
          showToast(err.message || 'Failed to reactivate user', 'error');
        }
      },
    });
  };

  // --- Reset Password ---

  const confirmResetPassword = (u: UserAdminProfile) => {
    setConfirmDialog({
      isOpen: true,
      title: t('admin.users.confirmResetPassword'),
      message: t('admin.users.confirmResetPasswordMessage', { name: u.fullName, email: u.email }),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          await userService.resetUserPassword(u.uid);
          showToast(t('admin.users.resetPasswordSuccess', { email: u.email }), 'success');
        } catch (err: any) {
          showToast(t('admin.users.resetPasswordError'), 'error');
        }
      },
    });
  };

  // --- Toggle MFA ---

  const confirmToggleMfa = (u: UserAdminProfile) => {
    const enabling = !u.mfaRequired;
    setConfirmDialog({
      isOpen: true,
      title: enabling ? t('admin.users.enableMfa') : t('admin.users.disableMfa'),
      message: enabling
        ? t('admin.users.enableMfaMessage', { name: u.fullName })
        : t('admin.users.disableMfaMessage', { name: u.fullName }),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          await userService.toggleMfa(u.uid, enabling);
          showToast(
            enabling ? t('admin.users.mfaEnabled', { name: u.fullName }) : t('admin.users.mfaDisabled', { name: u.fullName }),
            'success'
          );
          loadUsers();
        } catch (err: any) {
          showToast(err.message || 'Failed to toggle MFA', 'error');
        }
      },
    });
  };

  // --- Helpers ---

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'inactive': return 'status-badge status-inactive';
      case 'pending': return 'status-badge status-pending';
      default: return 'status-badge';
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t('admin.users.never');
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
  };

  const isOwnRow = (u: UserAdminProfile) => u.uid === user?.id;
  const isEditing = (u: UserAdminProfile) => u.uid === editingUid;

  // --- Render ---

  if (loading && users.length === 0) {
    return (
      <div className="tab-loading">
        <div className="spinner" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="users-panel">
      <div className="users-panel-header">
        <h2>{t('admin.users.title')}</h2>
        <button className="primary-button" onClick={openInviteModal}>
          + {t('admin.users.inviteUser')}
        </button>
      </div>

      {error && (
        <div className="users-error-banner">
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('admin.users.name')}</th>
              <th>{t('admin.users.jobTitle')}</th>
              <th>{t('admin.users.email')}</th>
              <th>{t('admin.users.role')}</th>
              {isMultiModule && <th>{t('admin.users.modules')}</th>}
              <th title={t('admin.users.designatedSignatoryTooltip')}>{t('admin.users.designatedSignatory')}</th>
              <th title={t('admin.users.selfApproveHint')}>{t('admin.users.selfApprove')}</th>
              <th>{t('admin.users.mfa')}</th>
              <th>{t('admin.users.mcpColumn')}</th>
              <th>{t('admin.users.status')}</th>
              <th>{t('admin.users.lastLogin')}</th>
              <th>
                {t('admin.users.actions')}
                {hasActiveFilters && (
                  <button className="clear-filters-btn" onClick={clearFilters} title={t('admin.users.clearFilters')}>
                    ✕
                  </button>
                )}
              </th>
            </tr>
            <tr className="filter-row">
              <th>
                <input
                  type="text"
                  className="filter-input"
                  placeholder={t('admin.users.filterName')}
                  value={filterName}
                  onChange={e => setFilterName(e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="filter-input"
                  placeholder={t('admin.users.filterJobTitle')}
                  value={filterJobTitle}
                  onChange={e => setFilterJobTitle(e.target.value)}
                />
              </th>
              <th>
                <input
                  type="text"
                  className="filter-input"
                  placeholder={t('admin.users.filterEmail')}
                  value={filterEmail}
                  onChange={e => setFilterEmail(e.target.value)}
                />
              </th>
              <th>
                <select className="filter-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
                  <option value="">{t('admin.users.filterAll')}</option>
                  {ASSIGNABLE_ROLES.map(role => (
                    <option key={role} value={role}>{t(`userMenu.roles.${role}`)}</option>
                  ))}
                </select>
              </th>
              {isMultiModule && (
                <th>
                  <select className="filter-select" value={filterModule} onChange={e => setFilterModule(e.target.value)}>
                    <option value="">{t('admin.users.filterAll')}</option>
                    <option value="FX">FX</option>
                    <option value="_fx_only">FX only</option>
                    <option value="Commodities">{t('admin.users.moduleCommodities')}</option>
                  </select>
                </th>
              )}
              <th>
                <select className="filter-select" value={filterSignatory} onChange={e => setFilterSignatory(e.target.value)}>
                  <option value="">{t('admin.users.filterAll')}</option>
                  <option value="yes">{t('admin.users.filterYes')}</option>
                  <option value="no">{t('admin.users.filterNo')}</option>
                </select>
              </th>
              <th>
                <select className="filter-select" value={filterSelfApprove} onChange={e => setFilterSelfApprove(e.target.value)}>
                  <option value="">{t('admin.users.filterAll')}</option>
                  <option value="yes">{t('admin.users.filterYes')}</option>
                  <option value="no">{t('admin.users.filterNo')}</option>
                </select>
              </th>
              <th>
                <select className="filter-select" value={filterMfa} onChange={e => setFilterMfa(e.target.value)}>
                  <option value="">{t('admin.users.filterAll')}</option>
                  <option value="required">{t('admin.users.mfaRequired')}</option>
                  <option value="off">{t('admin.users.mfaOff')}</option>
                </select>
              </th>
              <th>{/* MCP — no filter */}</th>
              <th>
                <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="">{t('admin.users.filterAll')}</option>
                  <option value="active">{t('admin.users.statusActive')}</option>
                  <option value="inactive">{t('admin.users.statusInactive')}</option>
                  <option value="pending">{t('admin.users.statusPending')}</option>
                </select>
              </th>
              <th>{/* Last Login — no filter */}</th>
              <th>{/* Actions — no filter */}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.uid} className={`${u.status === 'inactive' ? 'row-inactive' : ''} ${isEditing(u) ? 'row-editing' : ''}`}>
                {/* Name */}
                <td>
                  {isEditing(u) ? (
                    <div className="inline-edit-name">
                      <input
                        type="text"
                        className="inline-input"
                        value={editForm.firstName || ''}
                        onChange={e => setEditForm({ ...editForm, firstName: e.target.value })}
                        placeholder={t('admin.users.firstName')}
                      />
                      <input
                        type="text"
                        className="inline-input"
                        value={editForm.lastName || ''}
                        onChange={e => setEditForm({ ...editForm, lastName: e.target.value })}
                        placeholder={t('admin.users.lastName')}
                      />
                    </div>
                  ) : (
                    <>
                      {u.fullName}
                      {isOwnRow(u) && <span className="you-badge">{t('admin.users.you')}</span>}
                    </>
                  )}
                </td>

                {/* Job Title */}
                <td>
                  {isEditing(u) ? (
                    <input
                      type="text"
                      className="inline-input"
                      value={editForm.jobTitle || ''}
                      onChange={e => setEditForm({ ...editForm, jobTitle: e.target.value })}
                      placeholder={t('admin.users.jobTitlePlaceholder')}
                    />
                  ) : (
                    u.jobTitle || '—'
                  )}
                </td>

                {/* Email (never editable) */}
                <td>{u.email}</td>

                {/* Role */}
                <td>
                  {isEditing(u) ? (
                    <select
                      className="inline-select"
                      value={editForm.primaryRole || ''}
                      onChange={e => {
                        const newRole = e.target.value;
                        const updated = { ...editForm, primaryRole: newRole };
                        if (updated.moduleRoles) {
                          updated.moduleRoles = Object.fromEntries(
                            Object.keys(updated.moduleRoles).map(mod => [mod, newRole])
                          );
                        }
                        setEditForm(updated);
                      }}
                    >
                      {ASSIGNABLE_ROLES.map(role => (
                        <option key={role} value={role}>
                          {t(`userMenu.roles.${role}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    u.roleDisplayName || u.primaryRole || '—'
                  )}
                </td>

                {/* Modules */}
                {isMultiModule && (
                  <td>
                    {isEditing(u) ? (
                      <>
                        <label className="inline-checkbox-label">
                          <input
                            type="checkbox"
                            className="inline-checkbox"
                            checked={!!editForm.moduleRoles?.FX}
                            onChange={e => {
                              if (e.target.checked) {
                                setEditForm({ ...editForm, moduleRoles: { ...editForm.moduleRoles, FX: editForm.primaryRole || u.primaryRole || 'client_ro_user' } });
                              } else {
                                const { FX, ...rest } = editForm.moduleRoles || {};
                                setEditForm({ ...editForm, moduleRoles: Object.keys(rest).length ? rest : {} });
                              }
                            }}
                          />
                          FX
                        </label>
                        <label className="inline-checkbox-label">
                          <input
                            type="checkbox"
                            className="inline-checkbox"
                            checked={!!editForm.moduleRoles?.Commodities}
                            onChange={e => {
                              if (e.target.checked) {
                                setEditForm({ ...editForm, moduleRoles: { ...editForm.moduleRoles, Commodities: editForm.primaryRole || u.primaryRole || 'client_ro_user' } });
                              } else {
                                const { Commodities, ...rest } = editForm.moduleRoles || {};
                                setEditForm({ ...editForm, moduleRoles: Object.keys(rest).length ? rest : {} });
                              }
                            }}
                          />
                          {t('admin.users.moduleCommodities')}
                        </label>
                      </>
                    ) : (
                      (() => {
                        const modules: string[] = [];
                        if (hasFxAccess(u)) modules.push('FX');
                        if (u.moduleRoles) {
                          modules.push(...Object.keys(u.moduleRoles).filter(m => m !== 'FX'));
                        }
                        return modules.join(', ');
                      })()
                    )}
                  </td>
                )}

                {/* Designated Signatory */}
                <td className="center-cell">
                  {isEditing(u) ? (
                    <input
                      type="checkbox"
                      className="inline-checkbox"
                      checked={editForm.canSignDocuments || false}
                      onChange={e => setEditForm({ ...editForm, canSignDocuments: e.target.checked })}
                      disabled={!digitalSignatureEnabled}
                      title={!digitalSignatureEnabled ? t('admin.users.digitalSignatureNotEnabled', 'Digital signature is not enabled for this client') : undefined}
                    />
                  ) : (
                    u.canSignDocuments ? '✓' : '—'
                  )}
                </td>

                {/* Self-approve Modifications */}
                <td className="center-cell">
                  {isEditing(u) ? (
                    <input
                      type="checkbox"
                      className="inline-checkbox"
                      checked={editForm.canSelfApproveModifications || false}
                      onChange={e => setEditForm({ ...editForm, canSelfApproveModifications: e.target.checked })}
                      disabled={!['client_admin', 'client_fo_bo_user'].includes(editForm.primaryRole || '')}
                      title={!['client_admin', 'client_fo_bo_user'].includes(editForm.primaryRole || '') ? t('admin.users.selfApproveRoleRestriction') : undefined}
                    />
                  ) : (
                    u.canSelfApproveModifications ? '✓' : '—'
                  )}
                </td>

                {/* MFA */}
                <td className="center-cell">
                  <label
                    className="toggle-switch"
                    title={u.mfaRequired ? t('admin.users.disableMfa') : t('admin.users.enableMfa')}
                  >
                    <input
                      type="checkbox"
                      checked={u.mfaRequired}
                      onChange={() => confirmToggleMfa(u)}
                      disabled={isOwnRow(u) || isEditing(u) || !!editingUid || u.status === 'inactive'}
                    />
                    <span className="slider"></span>
                  </label>
                </td>

                {/* MCP */}
                <td
                  className="center-cell mcp-cell"
                  onClick={() => !isEditing(u) && !editingUid && setMcpModalUser(u)}
                  title={u.mcpKeyId ? '' : t('admin.users.mcpNoKey')}
                >
                  {u.mcpKeyId ? (
                    <span className={`mcp-badge ${u.mcpKeyIsActive === false ? 'mcp-badge-disabled' : 'mcp-badge-active'}`}>
                      {u.mcpKeyIsActive === false ? 'OFF' : 'ON'}
                    </span>
                  ) : (
                    <span className="mcp-badge mcp-badge-setup">+</span>
                  )}
                </td>

                {/* Status */}
                <td>
                  <span className={statusBadgeClass(u.status)}>
                    {t(`admin.users.status${u.status.charAt(0).toUpperCase() + u.status.slice(1)}`)}
                  </span>
                </td>

                {/* Last Login */}
                <td>{formatDate(u.lastLoginAt)}</td>

                {/* Actions */}
                <td className="actions-cell">
                  {isEditing(u) ? (
                    <>
                      <button
                        className="action-btn save"
                        onClick={saveEdit}
                        disabled={isSavingEdit}
                      >
                        {isSavingEdit ? t('admin.users.saving') : t('admin.users.save')}
                      </button>
                      <button
                        className="action-btn"
                        onClick={cancelEditing}
                        disabled={isSavingEdit}
                      >
                        {t('common.cancel')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="action-btn" onClick={() => startEditing(u)} disabled={!!editingUid}>
                        {t('admin.users.edit')}
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => confirmResetPassword(u)}
                        disabled={!!editingUid || u.status === 'inactive'}
                      >
                        {t('admin.users.resetPassword')}
                      </button>
                      {u.status === 'active' || u.status === 'pending' ? (
                        <button
                          className="action-btn danger"
                          onClick={() => confirmDeactivate(u)}
                          disabled={isOwnRow(u) || !!editingUid}
                          title={isOwnRow(u) ? t('admin.users.cannotDeactivateSelf') : ''}
                        >
                          {t('admin.users.deactivate')}
                        </button>
                      ) : (
                        <button className="action-btn" onClick={() => confirmReactivate(u)} disabled={!!editingUid}>
                          {t('admin.users.reactivate')}
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{t('admin.users.inviteUser')}</h3>
            <div className="form-group">
              <label>{t('admin.users.firstName')}</label>
              <input
                type="text"
                value={inviteForm.firstName}
                onChange={e => setInviteForm({ ...inviteForm, firstName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>{t('admin.users.lastName')}</label>
              <input
                type="text"
                value={inviteForm.lastName}
                onChange={e => setInviteForm({ ...inviteForm, lastName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>{t('admin.users.jobTitle')} <span className="optional-label">({t('common.optional')})</span></label>
              <input
                type="text"
                value={inviteForm.jobTitle || ''}
                onChange={e => setInviteForm({ ...inviteForm, jobTitle: e.target.value })}
                placeholder={t('admin.users.jobTitlePlaceholder')}
              />
            </div>
            <div className="form-group">
              <label>{t('admin.users.email')}</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={e => { setInviteForm({ ...inviteForm, email: e.target.value }); setInviteEmailError(null); }}
              />
              {inviteEmailError && <span className="field-error">{inviteEmailError}</span>}
            </div>
            <div className="form-group">
              <label>{t('admin.users.role')}</label>
              <select
                value={inviteForm.primaryRole}
                onChange={e => {
                  const newRole = e.target.value;
                  const updated: typeof inviteForm = { ...inviteForm, primaryRole: newRole };
                  // Keep module roles in sync with the selected role
                  if (updated.moduleRoles) {
                    updated.moduleRoles = Object.fromEntries(
                      Object.keys(updated.moduleRoles).map(mod => [mod, newRole])
                    );
                  }
                  setInviteForm(updated);
                }}
              >
                {ASSIGNABLE_ROLES.map(role => (
                  <option key={role} value={role}>
                    {t(`userMenu.roles.${role}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={inviteForm.canSignDocuments}
                  onChange={e => setInviteForm({ ...inviteForm, canSignDocuments: e.target.checked })}
                  disabled={!digitalSignatureEnabled}
                />
                {t('admin.users.designatedSignatoryLabel')}
              </label>
              <span className="hint-text">
                {digitalSignatureEnabled
                  ? t('admin.users.designatedSignatoryHint')
                  : t('admin.users.digitalSignatureNotEnabled', 'Digital signature is not enabled for this client')}
              </span>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={inviteForm.canSelfApproveModifications}
                  onChange={e => setInviteForm({ ...inviteForm, canSelfApproveModifications: e.target.checked })}
                  disabled={!['client_admin', 'client_fo_bo_user'].includes(inviteForm.primaryRole)}
                />
                {t('admin.users.selfApproveLabel')}
              </label>
              <span className="hint-text">
                {['client_admin', 'client_fo_bo_user'].includes(inviteForm.primaryRole)
                  ? t('admin.users.selfApproveHint')
                  : t('admin.users.selfApproveRoleRestriction')}
              </span>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={inviteForm.mfaRequired}
                  onChange={e => setInviteForm({ ...inviteForm, mfaRequired: e.target.checked })}
                />
                {t('admin.users.requireMfaLabel')}
              </label>
              <span className="hint-text">{t('admin.users.requireMfaHint')}</span>
            </div>
            {isMultiModule && (
              <div className="form-group checkbox-group">
                <span className="form-label">{t('admin.users.modules')}</span>
                <label>
                  <input
                    type="checkbox"
                    checked={inviteForm.moduleRoles?.FX !== undefined ? !!inviteForm.moduleRoles.FX : true}
                    onChange={e => {
                      if (e.target.checked) {
                        setInviteForm({ ...inviteForm, moduleRoles: { ...inviteForm.moduleRoles, FX: inviteForm.primaryRole } });
                      } else {
                        const { FX, ...rest } = inviteForm.moduleRoles || {};
                        setInviteForm({ ...inviteForm, moduleRoles: Object.keys(rest).length ? rest : undefined });
                      }
                    }}
                  />
                  FX
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={!!inviteForm.moduleRoles?.Commodities}
                    onChange={e => {
                      if (e.target.checked) {
                        setInviteForm({ ...inviteForm, moduleRoles: { ...inviteForm.moduleRoles, Commodities: inviteForm.primaryRole } });
                      } else {
                        const { Commodities, ...rest } = inviteForm.moduleRoles || {};
                        setInviteForm({ ...inviteForm, moduleRoles: Object.keys(rest).length ? rest : undefined });
                      }
                    }}
                  />
                  {t('admin.users.moduleCommodities')}
                </label>
                <span className="hint-text">{t('admin.users.moduleCommoditiesHint')}</span>
              </div>
            )}
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setShowInviteModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="primary-button"
                onClick={handleInvite}
                disabled={isInviting || !inviteForm.email || !inviteForm.firstName || !inviteForm.lastName}
              >
                {isInviting ? t('admin.users.sending') : t('admin.users.send')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MCP Key Modal */}
      <McpKeyModal
        isOpen={!!mcpModalUser}
        clientId={user?.organization?.id || ''}
        userUid={mcpModalUser?.uid || ''}
        userName={mcpModalUser?.fullName || ''}
        onClose={() => setMcpModalUser(null)}
        onSuccess={(msg) => showToast(msg, 'success')}
        onError={(msg) => showToast(msg, 'error')}
        onKeyChanged={() => loadUsers()}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        type="warning"
      />

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast(prev => ({ ...prev, visible: false }))}
      />
    </div>
  );
};

export default UsersPanel;
